import React, { useEffect, useState, useRef } from 'react';
import {
  Box,
  LinearProgress,
  Fade,
  Typography,
  Button,
  Collapse,
  IconButton,
  Divider,
} from '@mui/material';
import { useAccessToken } from '@features/user';
import APIService from '../../services/APIService';
import ThinkingAnimation from '../Common/ThinkingAnimation';
import BaseBlock from '../Patient/Blocks/BaseBlock';
import type { NoteStatusType } from '../../Views/NoteProcessingView';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useNavigate } from 'react-router-dom';
import { AnimatedCheckmark } from '../SVGIcon';
import SentryService from '@services/SentryService';
import { useEventTracking } from '@services/statsig';

interface NoteProcessingProgressProps {
  noteId: string;
  onComplete?: (noteId: string) => void;
  onError?: (error: string) => void;
}

const NoteProcessingProgress: React.FC<NoteProcessingProgressProps> = ({
  noteId,
  onComplete,
  onError,
}) => {
  const { getAccessToken } = useAccessToken();
  const [processingStatus, setProcessingStatus] = useState<NoteStatusType>();
  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showDetails, setShowDetails] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const phrasesGeneratedRef = useRef(false);
  const navigate = useNavigate();

  // Analytics hooks
  const { note, performance, error } = useEventTracking();
  const processingStartTimeRef = useRef<number | undefined>(undefined);
  const sectionTimersRef = useRef<Map<string, number>>(new Map());

  const stockPhrases = [
    'Analyzing your session recording...',
    'Processing clinical information...',
    'Organizing your documentation...',
    'Building your note...',
    'Structuring the content...',
    'Reviewing the details...',
    'Formatting sections...',
    'Compiling information...',
  ];

  const [animationWords, setAnimationWords] = useState<string[]>(stockPhrases);

  // Generate dynamic phrases mixing section names with stock phrases
  const generateMixedPhrases = (tasks: NoteStatusType['processing_tasks']) => {
    const actionVerbs = ['Analyzing', 'Creating', 'Building', 'Processing', 'Preparing', 'Writing'];

    // Get section names (excluding transcription and analysis)
    const sectionTasks = tasks
      .filter(
        task =>
          task.name !== 'transcription' &&
          task.name !== 'analysis' &&
          (task.status === 'processing' || task.status === 'completed'),
      )
      .map(task => task.name);

    if (sectionTasks.length === 0) {
      return stockPhrases;
    }

    // Create phrases for 2-3 sections with varying verbs
    const sectionPhrases = sectionTasks.map((taskName, index) => {
      const verb = actionVerbs[index % actionVerbs.length];
      return `${verb} ${taskName}...`;
    });

    // Create a pool of all phrases (excluding the first stock phrase)
    const allPhrases = [
      ...sectionPhrases,
      ...stockPhrases.slice(1), // Exclude "Analyzing your session recording..."
    ];

    // Shuffle the phrases
    const shuffledPhrases = allPhrases.sort(() => Math.random() - 0.5);

    // Take a subset (e.g., 6-8 phrases) to keep rotation interesting
    const selectedPhrases = shuffledPhrases.slice(0, Math.min(7, shuffledPhrases.length));

    // Always start with "Analyzing your session recording..."
    const mixedPhrases = ['Analyzing your session recording...', ...selectedPhrases];

    return mixedPhrases;
  };

  // Set encounter phase tag when component mounts for polling
  useEffect(() => {
    SentryService.setTag('encounterPhase', 'note-generation');
    return () => {
      SentryService.setTag('encounterPhase', '');
    };
  }, []);

  const fetchNoteProcessingStatus = async () => {
    try {
      const accessToken = await getAccessToken();
      const response = await APIService.makeAPIGetRequest({
        requestString: `/noteProcessingSession/status/${noteId}`,
        accessToken: accessToken,
      });

      if (response.ok) {
        const data = response.value as NoteStatusType;
        setProcessingStatus(data);

        // Start overall timer on first fetch
        if (!processingStartTimeRef.current) {
          processingStartTimeRef.current = Date.now();
          note.startGeneration({
            source: 'recording',
          });
        }

        // Track individual section progress
        data.processing_tasks.forEach(task => {
          // Start timer for new processing tasks
          if (task.status === 'processing' && !sectionTimersRef.current.has(task.name)) {
            // Start timing this section
            sectionTimersRef.current.set(task.name, performance.now());
          }
          
          // End timer for completed tasks
          if (task.status === 'completed' && sectionTimersRef.current.has(task.name)) {
            // End timing and track
            const startTime = sectionTimersRef.current.get(task.name);
            const duration = startTime ? performance.now() - startTime : undefined;
            if (duration) {
              performance.timing(`section_${task.name}`, duration, {
                note_id: noteId,
              });
              sectionTimersRef.current.delete(task.name);
            }
          }
        });

        // Only generate phrases once when we first get section data
        if (!phrasesGeneratedRef.current && data.processing_tasks.length > 2) {
          setAnimationWords(generateMixedPhrases(data.processing_tasks));
          phrasesGeneratedRef.current = true;
        }

        // Handle completion when analysis task is done.
        const analysisTask = data.processing_tasks.find(task => task.name === 'analysis');

        if (!isCompleted && analysisTask && analysisTask.status === 'completed') {
          // Mark as completed and navigate after a short delay
          setIsCompleted(true);
          
          // Track completion metrics
          const totalDuration = processingStartTimeRef.current 
            ? Date.now() - processingStartTimeRef.current 
            : 0;
          
          const completedTasks = data.processing_tasks.filter(t => t.status === 'completed').length;
          
          // Single event call with all metadata
          note.completeGeneration({
            section_count: completedTasks,
            note_id: noteId,
            total_duration_ms: String(totalDuration),
            sections_generated: String(completedTasks),
          });
          
          setTimeout(() => {
            onComplete?.(noteId);
          }, 2000);
        } else if (data.job_status === 'errored') {
          setIsError(true);
          let errorMsg = '';
          if (data.error_message === 'NO_SPOKEN_WORDS') {
            errorMsg =
              'No spoken words detected in audio. Please check your microphone settings before your next recording.';
          } else if (data.error_message === 'ANALYSIS_ERROR') {
            errorMsg = 'An unexpected error encountered.';
          } else {
            errorMsg = data.error_message || 'An error occurred during processing.';
          }
          setErrorMessage(errorMsg);
          
          // Track error
          error.track(errorMsg, 'note_processing', {
            severity: data.error_message === 'NO_SPOKEN_WORDS' ? 'medium' : 'high',
            note_id: noteId,
            error_type: data.error_message,
          });
          
          onError?.(errorMsg);
        }
        // Fallback: if the overall job status is completed (e.g., very quick run or polling delay)
        if (!isCompleted && data.job_status === 'completed') {
          setIsCompleted(true);
          
          // Track completion if not already tracked
          if (!analysisTask || analysisTask.status !== 'completed') {
            const totalDuration = processingStartTimeRef.current 
              ? Date.now() - processingStartTimeRef.current 
              : 0;
            
            const completedTasks = data.processing_tasks.filter(t => t.status === 'completed').length;
            
            // Single event call with all metadata
            note.completeGeneration({
              section_count: completedTasks,
              note_id: noteId,
              total_duration_ms: String(totalDuration),
              sections_generated: String(completedTasks),
            });
          }
          
          setTimeout(() => {
            onComplete?.(noteId);
          }, 2000);
        }
      }
    } catch (error) {
      console.error('Error fetching processing status:', error);
      SentryService.captureException(error, {
        level: 'error',
        tags: { component: 'NoteProcessingProgress', encounterPhase: 'note-generation' },
        extra: { noteId },
      });
      setIsError(true);
      setErrorMessage('Failed to fetch processing status');
      onError?.('Failed to fetch processing status');
    }
  };

  useEffect(() => {
    if (!isError && !isCompleted) {
      fetchNoteProcessingStatus();
    }
  }, [noteId]);

  useEffect(() => {
    if (!isError && !isCompleted) {
      const intervalId = setInterval(fetchNoteProcessingStatus, 3000);
      return () => clearInterval(intervalId);
    }
  }, [noteId, isError, isCompleted]);

  return (
    <Box sx={{ width: '100%', maxWidth: 600, mx: 'auto' }}>
      {/* Note Title */}
      {processingStatus?.note_title && (
        <Typography variant="h4" align="center" sx={{ mb: 3, fontWeight: 500 }}>
          {processingStatus.note_title}
        </Typography>
      )}

      <BaseBlock
        sx={{
          width: '100%',
          p: 4,
        }}
      >
        {isCompleted ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 3,
              py: 4,
            }}
          >
            <AnimatedCheckmark />
            <Typography variant="h6" sx={{ fontWeight: 500 }}>
              Done!
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Redirecting to your note...
            </Typography>
          </Box>
        ) : isError ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 3,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
              Error Processing Note
            </Typography>

            <Typography sx={{ mb: 1, textAlign: 'center' }}>{errorMessage}</Typography>

            <Typography sx={{ mb: 3, textAlign: 'center', color: 'text.secondary' }}>
              Our engineering team has been alerted and we'll do our best to recover this encounter
              soon.
            </Typography>

            {processingStatus?.errors && processingStatus.errors.length > 0 && (
              <Box
                sx={{
                  mb: 3,
                  width: '100%',
                  bgcolor: 'background.paper',
                  opacity: 0.9,
                  borderRadius: 1,
                  p: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                      Technical Details{' '}
                      <Typography
                        component="span"
                        variant="caption"
                        sx={{
                          color: 'text.secondary',
                          ml: 0.5,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        (for support)
                      </Typography>
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => setShowDetails(!showDetails)}
                      sx={{ ml: 1 }}
                    >
                      {showDetails ? (
                        <ExpandLessIcon fontSize="small" />
                      ) : (
                        <ExpandMoreIcon fontSize="small" />
                      )}
                    </IconButton>
                  </Box>
                </Box>
                <Collapse in={showDetails}>
                  <Divider sx={{ my: 1 }} />
                  <Box
                    sx={{
                      mt: 1,
                      p: 1,
                      bgcolor: 'grey.50',
                      borderRadius: 1,
                      overflow: 'auto',
                      maxHeight: '200px',
                    }}
                  >
                    {processingStatus.errors.map((err, index) => (
                      <Typography
                        key={index}
                        variant="body2"
                        sx={{
                          fontFamily: 'monospace',
                          fontSize: '0.85rem',
                          mb: 0.5,
                          color: 'error.main',
                        }}
                      >
                        • {err.error}
                      </Typography>
                    ))}
                  </Box>
                </Collapse>
              </Box>
            )}

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button variant="contained" color="primary" onClick={() => navigate('/')}>
                Back to Dashboard
              </Button>
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 3,
            }}
          >
            {/* Thinking Animation */}
            <Fade in={true} timeout={1000}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  minHeight: 24, // Fixed height to prevent layout shift
                }}
              >
                <ThinkingAnimation words={animationWords} />
              </Box>
            </Fade>

            {/* Progress Bar */}
            <Box sx={{ width: '100%' }}>
              <LinearProgress
                variant="indeterminate"
                sx={{
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: 'rgba(0, 0, 0, 0.08)',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 4,
                    backgroundColor: 'primary.main',
                    transition: 'transform 0.4s ease',
                  },
                }}
              />
            </Box>
          </Box>
        )}
      </BaseBlock>
    </Box>
  );
};

export default NoteProcessingProgress;
